import { TokenUsageView } from "@/api/types";

/** Compact token count: 1234 → "1.2k", 8000 → "8k", 2_000_000 → "2M". */
function fmt(n: number): string {
  if (n >= 1_000_000) return `${trim(n / 1_000_000)}M`;
  if (n >= 1_000) return `${trim(n / 1_000)}k`;
  return String(n);
}

function trim(n: number): string {
  // One decimal, but drop a trailing ".0".
  return n.toFixed(1).replace(/\.0$/, "");
}

/** "used" alone, or "used/cap" when a cap is set. */
function part(used: number, cap: number | null): string {
  return cap == null ? fmt(used) : `${fmt(used)}/${fmt(cap)}`;
}

/** Is the user at/over either cap? Drives the muted→warning color. */
function over(u: TokenUsageView): boolean {
  return (
    (u.dailyLimit != null && u.usedToday >= u.dailyLimit) ||
    (u.monthlyLimit != null && u.usedMonth >= u.monthlyLimit)
  );
}

/**
 * Per-user token consumption shown in the chat footer. Shows used/cap when an
 * admin has set a limit, else just the running totals. Turns amber at the cap.
 */
export function TokenUsageBadge({ usage }: { usage: TokenUsageView | null }) {
  if (!usage) return null;
  const danger = over(usage);
  return (
    <span
      className={
        danger
          ? "text-xs font-medium text-amber-600"
          : "text-xs text-foreground"
      }
      title="LLM tokens used today and this month (UTC). Caps are set by an admin."
    >
      Tokens — Today {part(usage.usedToday, usage.dailyLimit)} · Month{" "}
      {part(usage.usedMonth, usage.monthlyLimit)}
    </span>
  );
}
